import type { Dispatch, SetStateAction } from 'react';
import Card from '@components/UI/Card';
import Button from '@components/UI/Button';
import { DeleteButton, EditButton, EmptyState } from '@components/UI';
import { showSpinner, hideSpinner } from '@components/Layout/LoadingSpinner';
import type { TradeFilter, TradingProfile } from '@einhaus/project314-types';
import type { NewTradeFilterState, SaveTradingProfileSettings } from './types';

interface TradeFiltersSectionProps {
    tradeFilters: TradeFilter[];
    setTradeFilters: Dispatch<SetStateAction<TradeFilter[]>>;
    formData: TradingProfile;
    setFormData: Dispatch<SetStateAction<TradingProfile>>;
    tradingProfileId: number;
    saveTradeFilter: SaveTradingProfileSettings;
    setEditingTradeFilterId: Dispatch<SetStateAction<string | null>>;
    setNewTradeFilter: Dispatch<SetStateAction<NewTradeFilterState>>;
    setShowTradeFilterModal: Dispatch<SetStateAction<boolean>>;
}

const TradeFiltersSection = ({
    tradeFilters,
    setTradeFilters,
    formData,
    setFormData,
    tradingProfileId,
    saveTradeFilter,
    setEditingTradeFilterId,
    setNewTradeFilter,
    setShowTradeFilterModal
}: TradeFiltersSectionProps) => {
    return (
        <Card className="mb-4" headerClassName="flex justify-between items-center" title="Trade Filters">
            <Button
                variant="outline-dark"
                onClick={() => {
                    setEditingTradeFilterId(null);

                    setNewTradeFilter({
                        entityType: 'Asset',
                        tradeDirection: 'either',
                        property: '',
                        comparison: 'greaterThan',
                        value: null
                    });

                    setShowTradeFilterModal(true);
                }}
            >
                + Add
            </Button>

            {tradeFilters.length === 0 ? (
                <EmptyState size="sm" title="No trade filters added." />
            ) : (
                <div className="overflow-x-auto">
                    <table className="table-auto w-full text-white border-collapse">
                        <thead>
                            <tr>
                                <th className="px-4 py-2 text-left font-medium">Property</th>
                                <th className="px-4 py-2 text-left font-medium">Comparison</th>
                                <th className="px-4 py-2 text-left font-medium">Value</th>
                                <th className="px-4 py-2 text-left font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tradeFilters.map((filter, index) => (
                                <tr
                                    className={`border-b border-zinc-700 hover:bg-zinc-700 ${
                                        index % 2 === 0 ? 'bg-zinc-800' : 'bg-zinc-750'
                                    }`}
                                    key={filter.id}
                                >
                                    <td className="px-4 py-2">{filter.field}</td>
                                    <td className="px-4 py-2">
                                        {filter.operator === 'greaterThan' && '>'}
                                        {filter.operator === 'lessThan' && '<'}
                                        {filter.operator === 'greaterThanEqual' && '>='}
                                        {filter.operator === 'lessThanEqual' && '<='}
                                        {filter.operator === 'equal' && '='}
                                    </td>
                                    <td className="px-4 py-2">{filter.value}</td>
                                    <td className="px-4 py-2">
                                        <div className="flex items-center gap-2">
                                            <EditButton
                                                size="sm"
                                                onClick={() => {
                                                    setEditingTradeFilterId(filter.id);

                                                    setNewTradeFilter({
                                                        entityType: filter.entityType,
                                                        tradeDirection: filter.tradeDirection,
                                                        property: filter.field,
                                                        comparison: filter.operator,
                                                        value: filter.value ?? null
                                                    });

                                                    setShowTradeFilterModal(true);
                                                }}
                                            />
                                            <DeleteButton
                                                size="sm"
                                                onClick={() => {
                                                    const updatedTradeFilters = tradeFilters.filter((f) => f.id !== filter.id);
                                                    setTradeFilters(updatedTradeFilters);

                                                    // Update the form data with the trade filter removed
                                                    const updatedFormData = {
                                                        ...formData,
                                                        tradeFilters: updatedTradeFilters
                                                    };

                                                    setFormData(updatedFormData);
                                                    showSpinner();

                                                    // Use the trade filter mutation to save the updated settings
                                                    saveTradeFilter(
                                                        {
                                                            tradingProfileId,
                                                            settings: updatedFormData
                                                        },
                                                        {
                                                            onSuccess: () => {
                                                                hideSpinner();
                                                            },
                                                            onError: () => {
                                                                hideSpinner();
                                                            }
                                                        }
                                                    );
                                                }}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Card>
    );
};

export default TradeFiltersSection;
